import { X } from 'lucide-react';
import { useState } from 'react';

import {
    echoedOutreachWindow,
    OUTREACH_ANALYTICS_TIMEZONE,
    OUTREACH_REFRESH_INTERVAL_MS,
    type OutreachTabProps,
    outreachWindowSummary,
} from '@apps/web/components/admin/outreach_observability/constants';
import { JudgeFlaggedRunsTable } from '@apps/web/components/admin/outreach_observability/judge_flagged_runs_table';
import {
    humanizeJudgeLabel,
    JudgeQualityPanel,
} from '@apps/web/components/admin/outreach_observability/judge_quality_panel';
import { JudgeRunsDialog } from '@apps/web/components/admin/outreach_observability/judge_runs_dialog';
import {
    Card,
    CardContent,
    CardDescription,
    CardHeader,
    CardTitle,
} from '@apps/web/components/ui/card';
import { formatDateKey } from '@apps/web/lib/date_utils';
import { cn } from '@apps/web/lib/utils';
import { useAdminOutreachObservability } from '@apps/web/swr_data_fetching/data_fetching_hooks';
import { type JudgeFlagCount } from '@libraries/typescript/api_client';

type JudgeDrill = {
    readonly title: string;
    readonly grade?: string;
    readonly day?: string;
};

function JudgeFlagsPanel({
    judgeFlags,
    judgedRunCount,
    windowSummary,
}: {
    readonly judgeFlags: Array<JudgeFlagCount>;
    readonly judgedRunCount: number;
    readonly windowSummary: string;
}) {
    return (
        <Card>
            <CardHeader>
                <CardTitle>Judge flags (window snapshot)</CardTitle>
                <CardDescription>
                    {judgedRunCount} runs judged ({windowSummary}).
                </CardDescription>
            </CardHeader>
            <CardContent className="flex flex-col gap-2">
                {judgeFlags.map((flag) => (
                    <div
                        key={flag.label}
                        className="flex items-center justify-between gap-2 text-sm"
                    >
                        <span className="text-muted-foreground min-w-0 truncate">
                            {humanizeJudgeLabel(flag.label)}
                        </span>
                        <span
                            className={cn(
                                'font-semibold tabular-nums',
                                flag.count > 0 &&
                                    'text-amber-600 dark:text-amber-400',
                            )}
                        >
                            {flag.count}
                        </span>
                    </div>
                ))}
            </CardContent>
        </Card>
    );
}

export function QualityTab({
    organizationIds,
    window: liveWindow,
    urgency,
}: OutreachTabProps) {
    const { data: responseData } = useAdminOutreachObservability(
        {
            sections: ['live', 'trend', 'judge_stats'],
            organizationIds,
            window: liveWindow,
            urgency,
        },
        { refreshIntervalMs: OUTREACH_REFRESH_INTERVAL_MS },
    );
    const data = responseData?.urgency === urgency ? responseData : undefined;
    const windowSummary = outreachWindowSummary(
        (data ? echoedOutreachWindow(data) : null) ?? liveWindow,
    );
    const [judgeDrill, setJudgeDrill] = useState<JudgeDrill | null>(null);
    const [flagFilter, setFlagFilter] = useState<string | null>(null);
    const allFlagged = data?.judge_stats?.top_flagged ?? [];
    const filteredFlagged =
        flagFilter === null
            ? allFlagged
            : allFlagged.filter((run) => run.labels.includes(flagFilter));
    return (
        <div className="flex flex-col gap-4">
            <JudgeQualityPanel
                judgeStats={data?.judge_stats ?? null}
                trendDays={data?.trend ?? []}
                onGradeDayClick={(grade, day) =>
                    setJudgeDrill({
                        title: `${humanizeJudgeLabel(grade)} runs on ${formatDateKey(
                            day,
                            OUTREACH_ANALYTICS_TIMEZONE,
                        )}`,
                        grade,
                        day,
                    })
                }
                onFlagClick={(flag) => setFlagFilter(flag)}
            />
            <div className="grid gap-4 lg:grid-cols-3">
                <div className="lg:col-span-1">
                    <JudgeFlagsPanel
                        judgeFlags={data?.judge_flags ?? []}
                        judgedRunCount={data?.judged_run_count ?? 0}
                        windowSummary={windowSummary}
                    />
                </div>
                <Card className="lg:col-span-2">
                    <CardHeader>
                        <CardTitle>Flagged runs (last 14 days)</CardTitle>
                        <CardDescription>
                            Runs the LLM judge flagged, newest first, with grade
                            and flag labels.
                        </CardDescription>
                    </CardHeader>
                    <CardContent className="flex flex-col gap-2">
                        {flagFilter !== null ? (
                            <div className="flex items-center gap-2 text-xs">
                                <span className="text-muted-foreground">
                                    Filtered by
                                </span>
                                <button
                                    type="button"
                                    onClick={() => setFlagFilter(null)}
                                    className="bg-secondary text-secondary-foreground hover:bg-secondary/80 inline-flex items-center gap-1 rounded-md px-2 py-0.5 font-medium"
                                >
                                    {humanizeJudgeLabel(flagFilter)}
                                    <X className="size-3" />
                                </button>
                            </div>
                        ) : null}
                        <JudgeFlaggedRunsTable runs={filteredFlagged} />
                    </CardContent>
                </Card>
            </div>
            <JudgeRunsDialog
                open={judgeDrill !== null}
                onOpenChange={(open) => {
                    if (!open) {
                        setJudgeDrill(null);
                    }
                }}
                title={judgeDrill?.title ?? ''}
                organizationIds={organizationIds}
                urgency={urgency}
                grade={judgeDrill?.grade}
                day={judgeDrill?.day}
            />
        </div>
    );
}
